import axios from "axios";
import { createS3UploadConfig } from "@/utils/s3Utils";
import { GR_STATUS } from "@/constants/productStock";

function mapListItem(item) {
  return {
    id: item.id,
    grCode: item.grNo || "",
    poRefCode: item.refDocNo || "",
    vendorCode: item.vendorNo || "",
    vendorName: item.vendorName || "",
    branchName: item.branch?.name || "",
    createdAt: item.creationDt || "",
    receivedDt: item.receivedDt || "",
    status: item.status || "",
    syncDt: item.syncDt || null,
    needHqApproval: Boolean(item.needHqApproval),
    items: [],
  };
}

function mapDetailItem(item) {
  return {
    id: item.id,
    poId: item.poId || null,
    grCode: item.grNo || "",
    poRefCode: item.refDocNo || "",
    vendorCode: item.vendorNo || "",
    vendorName: item.vendorName || "",
    branchName: item.branch?.name || "",
    createdAt: item.creationDt || "",
    receivedDt: item.receivedDt || "",
    status: item.status || "",
    syncDt: item.syncDt || null,
    needHqApproval: Boolean(item.needHqApproval),
    rejectReason: item.rejectReason || null,
    files: (item.mediaFiles || []).map(f => ({
      id: f.id,
      originalName: f.metadata?.originalname || f.originalName || "",
      contentType: f.fileType || "",
      fileSize: f.fileSize || 0,
      status: f.status || "",
    })),
    items: (item.goodReceiveProducts || []).map(p => ({
      productId: p.productId,
      productCode: p.product?.no || "",
      productNameMain: p.product?.description || "",
      productType: p.productType || "NORMAL",
      quantity: p.qtyUom,
      qtyBaseUom: p.qtyBaseUom ?? null,
      qtyPerUnitOfMeasure: p.qtyPerUnitOfMeasure ?? null,
      uomId: p.uomId,
      uomName: p.uom?.description || p.product?.baseUom?.description || "",
      uom: p.uom || null,
      baseUomId: p.product?.baseUomId ?? null,
      baseUomName: p.product?.baseUom?.description || "",
      totalPrice: p.totalCost || 0,
      lotNumber: p.lotNo || "",
      expiredDt: p.expiredDt || null,
    })),
  };
}

const moduleGoodsReceive = {
  namespaced: true,
  state: {
    goodsReceiveList: [],
    hqGoodsReceiveList: [],
  },
  mutations: {
    setGoodsReceiveList(state, data) {
      state.goodsReceiveList = data;
    },
    setHQGoodsReceiveList(state, data) {
      state.hqGoodsReceiveList = data;
    },
    patchHQGoodsReceiveStatus(state, { id, status }) {
      const item = state.hqGoodsReceiveList.find(i => i.id === id);
      if (item) item.status = status;
    },
    removeHQGoodsReceive(state, { id }) {
      state.hqGoodsReceiveList = state.hqGoodsReceiveList.filter(i => i.id !== id);
    },
  },
  actions: {
    async fetchGoodsReceiveList({ commit }, { clinicUrl, branchUrl }) {
      try {
        const res = await axios.get(
          `/api/goodreceive/${clinicUrl}/${branchUrl}`
        );
        const mappedData = (res.data.data || []).map(mapListItem);
        commit("setGoodsReceiveList", mappedData);
        return mappedData;
      } catch (err) {
        console.error("fetchGoodsReceiveList failed", err);
        throw err;
      }
    },
    async fetchGoodsReceiveById(_, { clinicUrl, branchUrl, id }) {
      try {
        const res = await axios.get(
          `/api/goodreceive/${clinicUrl}/${branchUrl}/${id}`
        );
        return mapDetailItem(res.data.data);
      } catch (err) {
        console.error("fetchGoodsReceiveById failed", err);
        throw err;
      }
    },
    createGoodsReceive(_, { clinicUrl, branchUrl, body }) {
      return axios
        .post(`/api/goodreceive/${clinicUrl}/${branchUrl}`, body, {
          skipGlobalErrorHandler: true,
        })
        .then(res => res)
        .catch(err => {
          throw err;
        });
    },
    updateGoodsReceive(_, { clinicUrl, branchUrl, id, body }) {
      return axios
        .put(`/api/goodreceive/${clinicUrl}/${branchUrl}/${id}`, body, {
          skipGlobalErrorHandler: true,
        })
        .then(res => res)
        .catch(err => {
          throw err;
        });
    },
    approveGoodsReceive(_, { clinicUrl, branchUrl, id }) {
      return axios
        .post(`/api/goodreceive/${clinicUrl}/${branchUrl}/${id}/approve`, {}, {
          skipGlobalErrorHandler: true,
        })
        .then(res => res)
        .catch(err => {
          throw err;
        });
    },
    hqApproveGoodsReceive(_, { clinicUrl, branchUrl, id, action, rejectReason }) {
      const body = { action, type: "GOOD_RECEIVE" };
      if (rejectReason) body.rejectReason = rejectReason;
      return axios
        .post(`/api/stockAdjustment/${clinicUrl}/${branchUrl}/${id}/hq-approve`, body, {
          skipGlobalErrorHandler: true,
        })
        .then(res => res)
        .catch(err => {
          throw err;
        });
    },
    deleteGoodsReceive(_, payload) {
      return axios
        .delete(
          `/api/goodreceive/${payload.clinicUrl}/${payload.branchUrl}/${payload.id}`,
          { skipGlobalErrorHandler: true }
        )
        .then(res => res)
        .catch(err => {
          throw err;
        });
    },
    async fetchMediaFileById(_, { clinicUrl, branchUrl, id }) {
      const res = await axios.get(
        `/api/mediaFile/${clinicUrl}/${branchUrl}`,
        { params: { ids: id } }
      );
      const payload = res?.data ?? {};
      const first =
        (Array.isArray(payload) && payload[0]) ||
        (Array.isArray(payload.data) && payload.data[0]) ||
        payload.data ||
        payload;
      return first?.mediaURL || first?.url || null;
    },
    async previewMediaFile({ dispatch }, { clinicUrl, branchUrl, id }) {
      if (!id) return { success: false, reason: "invalid" };
      const newTab = window.open("", "_blank");
      try {
        const mediaURL = await dispatch("fetchMediaFileById", {
          clinicUrl,
          branchUrl,
          id,
        });
        if (mediaURL && newTab) {
          newTab.location.href = mediaURL;
          return { success: true };
        }
        if (newTab) newTab.close();
        return { success: false, reason: "not_found" };
      } catch (err) {
        console.error("previewMediaFile failed", err);
        if (newTab) newTab.close();
        return { success: false, reason: "error" };
      }
    },
    async deleteMediaFile(_, { clinicUrl, branchUrl, id }) {
      return axios.delete(`/api/mediaFile/${clinicUrl}/${branchUrl}/${id}`);
    },
    async uploadGrFilesToS3(_, { uploadUrls, files }) {
      if (!files?.length || !uploadUrls?.length) return false;
      const results = await Promise.all(
        uploadUrls.map(async (target, i) => {
          const url = typeof target === "string" ? target : target.url;
          const originalName = typeof target === "string" ? null : target.originalName;
          const file =
            (originalName && files.find(f => f.name === originalName)) ||
            files[i] ||
            files[0];
          const config = createS3UploadConfig(url, file);
          const res = await axios.put(url, file, config);
          return res.status === 200;
        })
      );
      return results.every(Boolean);
    },
  },
  getters: {
    getGoodsReceiveList: state => {
      return state.goodsReceiveList;
    },
    getHQGoodsReceiveList: state => {
      return state.hqGoodsReceiveList;
    },
    isGrWaitingBmRedDot: state => {
      return state.goodsReceiveList.some(
        gr => gr.status === GR_STATUS.WAITING_BM
      );
    },
    isGrRejectedRedDot: state => {
      return state.goodsReceiveList.some(
        gr => gr.status === GR_STATUS.REJECTED
      );
    },
    // HQ approval red dot: GR items waiting for HQ (central "การอนุมัติ" menu).
    isGrWaitingHqRedDot: state => {
      return state.hqGoodsReceiveList.some(
        gr => gr.status === GR_STATUS.WAITING_HQ
      );
    },
  },
};

export default moduleGoodsReceive;
